import { useCallback, useMemo } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DirectionsCarIcon, LocalShippingIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { InfoItemButton, Typography } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import {
    SPEEDINGS_HEAVY_VEHICLE_ACTIVE_LAYER,
    SPEEDINGS_LIGHT_VEHICLE_ACTIVE_LAYER,
} from "../../constants/urlParams"
import { useSpeedingsContext } from "../../contexts/SpeedingsContext"
import { useSpeedingsGrades } from "../../hooks/queries/useSpeedingsGrades"
import { useTrafficStats } from "../../hooks/queries/useTrafficStats"

import * as Styled from "./TrafficSelector.styled"

export const TrafficSelector = (props) => {

    const {
        className,
        ...infoItemProps
    } = props

    const { road } = useParams()

    const [state, dispatch] = useSpeedingsContext()
    const { currentActiveLayer, currentYear } = state
    const { setCurrentActiveLayer } = dispatch
    const { t } = useTranslation(I18N_NAMESPACE)

    const { data: allYearsGrades } = useSpeedingsGrades({
        group_by: ["speed_limit", "year"],
        ...(road && { linear_location_road: road }),
    })

    const currentYearHasData = useMemo(() => {
        if (!allYearsGrades || !currentYear) {
            return false
        }
        const currentYearData = allYearsGrades.find(dataItem => {
            return dataItem.year === currentYear
        })
        return currentYearData?.weighted_avg !== undefined
    }, [currentYear, allYearsGrades])

    const {
        data: trafficData,
        isLoading: isLoadingTrafficData,
        isError: isErrorTrafficData,
    } = useTrafficStats({
        year: currentYear,
        ...(road && ({ linear_location_road: road })),
    }, {
        enabled: currentYearHasData,
    })

    const displayedElement = useCallback((key) => {
        return isLoadingTrafficData ? <Styled.Skeleton />
            : currentYearHasData && (isErrorTrafficData || trafficData?.[key] === undefined) ? <Styled.Error variant="Desktop & Mobile/Body/Regular">
                {t(I18N_NAMESPACE, "containers.trafficSelector.error")}
            </Styled.Error>
                : <Typography variant="Desktop & Mobile/Body/Regular">
                    {(currentYearHasData ? trafficData?.[key] : "-") + "/" + t(I18N_NAMESPACE, "containers.trafficSelector.daySymbol")}
                </Typography>
    }, [isErrorTrafficData, isLoadingTrafficData, t, trafficData, currentYearHasData])

    return (
        <div>
            <Styled.InfoItemButton
                className={className}
                disabled={!currentYearHasData}
                endAdornments={displayedElement("average_light_vehicle_count")}
                icon={DirectionsCarIcon}
                label={t(I18N_NAMESPACE, "containers.trafficSelector.lightVehicleLabel")}
                selected={currentActiveLayer === SPEEDINGS_LIGHT_VEHICLE_ACTIVE_LAYER}
                onClick={() => setCurrentActiveLayer(SPEEDINGS_LIGHT_VEHICLE_ACTIVE_LAYER)}
                {...infoItemProps}
            />
            <Styled.InfoItemButton
                className={className}
                disabled={!currentYearHasData}
                endAdornments={displayedElement("average_heavy_vehicle_count")}
                icon={LocalShippingIcon}
                label={t(I18N_NAMESPACE, "containers.trafficSelector.heavyVehicleLabel")}
                selected={currentActiveLayer === SPEEDINGS_HEAVY_VEHICLE_ACTIVE_LAYER}
                onClick={() => setCurrentActiveLayer(SPEEDINGS_HEAVY_VEHICLE_ACTIVE_LAYER)}
                {...infoItemProps}
            />
        </div>
    )
}

TrafficSelector.propTypes = InfoItemButton.propTypes & {
    className: PropTypes.string,
}